import { inject, Injectable, signal } from '@angular/core';
import { AuthService } from '@fc-core/services';
import { CameraEventConnection } from '../models/camera-event-connection';
import { CameraStatusStore } from '../store/camera-status.store';
import { environment } from '../../../../environments/environment';
import { CameraEventSnapshot } from '../models/camera-event-snapshot';
import { CameraEventNetwork } from '../models/camera-event-network';
import { CameraEventRecording } from '../models/camera-event-recording';
import { CameraEventDownload } from '../models/vss-player/camera-event-download';

type RawCameraEventDownload = Omit<CameraEventDownload, 'recordingId'> & {
  recordingId: string | number;
};
type DownloadEventPayload = RawCameraEventDownload | RawCameraEventDownload[];

function parseDownloadStatuses(data: string): CameraEventDownload[] {
  const payload = JSON.parse(data) as DownloadEventPayload | null;
  const statuses = Array.isArray(payload) ? payload : payload ? [payload] : [];

  return statuses.map((status) => ({
    ...status,
    recordingId: String(status.recordingId),
  }));
}

@Injectable({
  providedIn: 'root',
})
export class CamerasEventSourceService {
  private cameraStatusStore = inject(CameraStatusStore);
  private camerasEventSource: EventSource;
  private auth = inject(AuthService);
  snapshotsSignal = signal<CameraEventSnapshot | null>(null);
  downloadSignal = signal<CameraEventDownload[] | null>(null);
  cameraEvents = signal<number[] | null>(null);

  connectCamerasEvents() {
    this.camerasEventSource = new EventSource(
      `${environment.eventSourceHost}/ngdvr/?auth=${this.auth.token}`,
    );

    this.camerasEventSource.addEventListener('download', (event) => {
      const statuses = parseDownloadStatuses(event.data);

      this.downloadSignal.update((currentStatuses) => {
        const statusesByRecordingId = new Map(
          (currentStatuses ?? []).map((status) => [status.recordingId, status]),
        );

        statuses.forEach((status) => {
          statusesByRecordingId.set(status.recordingId, status);
        });

        return Array.from(statusesByRecordingId.values());
      });
    });
    this.camerasEventSource.addEventListener('connection', (event) => {
      const status: CameraEventConnection = JSON.parse(event.data);
      this.cameraStatusStore.setCameraStatus(status.cameraId, status);
      this.cameraEvents.set([status.cameraId]);
    });

    this.camerasEventSource.addEventListener('snapshot', (event) => {
      const snapshot: CameraEventSnapshot = JSON.parse(event.data);
      this.cameraStatusStore.setCameraSnapshot(+snapshot.cameraId, snapshot);
      this.snapshotsSignal.set(snapshot);
      this.cameraEvents.set([snapshot.cameraId]);
    });

    this.camerasEventSource.addEventListener('network', (event) => {
      const statuses: CameraEventNetwork[] = JSON.parse(event.data);
      statuses.forEach((status) => {
        this.cameraStatusStore.setCameraStatus(+status.cameraId, {
          ...status,
          cameraId: +status.cameraId,
        });
      });
      this.cameraEvents.set(statuses.map((s) => +s.cameraId));
    });

    this.camerasEventSource.addEventListener('recording', (event) => {
      const statuses: CameraEventRecording[] = JSON.parse(event.data);
      statuses.forEach((status) => {
        this.cameraStatusStore.setCameraStatus(+status.cameraId, {
          ...status,
          cameraId: +status.cameraId,
        });
      });
      this.cameraEvents.set(statuses.map((s) => +s.cameraId));
    });
  }

  disconnectCameraEvents() {
    this.camerasEventSource?.close();
  }
}
